import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import ReactConfetti from 'react-confetti';
import { apiClient } from '../api';
import { ALL_STORES, RETAILER_CONFIGS } from '../config/retailers';
import { useEnabledRetailers } from '../FeatureFlags';
import { useProgress } from '../contexts/ProgressContext';
import { useWindowDimensions } from '../hooks/useWindowDimensions';
import useAmazonDataConnection from '../hooks/useAmazonDataConnection';
import type { StoreConfig } from '../modules/Config';
import { clientLogger } from '../utils/client-logger';
import { LoadingPage } from './LoadingPage';
import { SignInPage } from './SignInPage';
import { SurveyQuestionInput } from './post-connect-survey/SurveyQuestionInput';
import type { SurveyAnswer, SurveyQuestion } from './post-connect-survey/types';
import {
  canProceedFromQuestion,
  getInitialAnswers,
  getNextVisibleIndex,
  serializeAnswers,
} from './post-connect-survey/surveyUtils';

const SURVEY_ID = 'cleaning-study';
const FALLBACK_SURVEY_VERSION = 'cleaning-study-v1';

type Screen = 'welcome' | 'connect' | 'survey' | 'done';
type ConnectStep = 'region' | 'signin' | 'connecting' | 'connected' | 'error';

const PARTICIPANT_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const genParticipantCode = () => {
  let out = '';
  for (let i = 0; i < 5; i += 1) {
    out +=
      PARTICIPANT_CHARSET[
        Math.floor(Math.random() * PARTICIPANT_CHARSET.length)
      ];
  }
  return out;
};

const rewriteUrl = (path: string) => {
  if (window.location.pathname !== path) {
    window.history.replaceState(null, '', path);
  }
};

export function CleaningStudyPage() {
  // The flow is linear and its state (connect progress, survey answers, the
  // signed-in user) can't be reconstructed from the URL, so a refresh/deep-link
  // to any sub-path always restarts at the welcome screen rather than dropping
  // the user into a half-initialized step. The URL-rewrite effect re-points the
  // address bar to /cleaning-study on mount. (Sub-paths stay registered in the
  // router only so a refresh resolves to this page instead of a 404.)
  const [screen, setScreen] = useState<Screen>('welcome');
  const [connectStep, setConnectStep] = useState<ConnectStep>('region');
  const [region, setRegion] = useState<'US' | 'CA'>('US');
  const [loadingStep, setLoadingStep] = useState(1);
  const [connectError, setConnectError] = useState<string | null>(null);

  const { setOverride } = useProgress();

  // ── Store selection: CA needs the amazon_ca store (server picks the tool by
  // store.region), US needs amazon_us. ────────────────────────────────────────
  const enabledRetailers = useEnabledRetailers();
  const amazonStores = useMemo(
    () =>
      ALL_STORES.filter(
        (s) =>
          s.retailer_id === 'amazon' &&
          enabledRetailers.includes(s.retailer_key)
      ),
    [enabledRetailers]
  );
  const store: StoreConfig = useMemo(() => {
    const wantKey = region === 'CA' ? 'amazon_ca' : 'amazon_us';
    return (
      amazonStores.find((s) => s.retailer_key === wantKey) ??
      amazonStores[0] ??
      ALL_STORES.find((s) => s.retailer_key === 'amazon_us')!
    );
  }, [amazonStores, region]);

  // ── Connect lifecycle (mirrors MainPage's reducer handlers) ─────────────────
  const handleConnectStart = useCallback(() => {
    setConnectError(null);
    setLoadingStep(1);
    setConnectStep('connecting');
  }, []);

  const handleAuthComplete = useCallback(() => {
    // Auth succeeded (incl. after follow-up/MFA). Don't wait on the purchase
    // stream — it runs fire-and-forget in the background and still persists
    // data server-side. Go to the survey.
    void apiClient.updateProxyIpAddress().catch((error) => {
      clientLogger.info('updateProxyIpAddress failed on AUTH_COMPLETE', {
        error,
      });
    });
    setScreen('survey');
  }, []);

  // Purchase stream keeps emitting after we've moved on — no-op in the UI.
  const handleSuccessConnect = useCallback(() => {}, []);

  const handleConnectionError = useCallback((errorDetails: string) => {
    clientLogger.info('Cleaning study connection failed', { errorDetails });
    setConnectError(errorDetails);
    setConnectStep('error');
  }, []);

  // NEED_SIGNIN / follow-up (MFA/OTP): the hook flips isFollowUpForm and calls
  // this — drop back to the signin screen so the FollowUpForm renders (instead
  // of staying on the loading view).
  const handleGoToInitial = useCallback(() => {
    setConnectStep('signin');
  }, []);

  const {
    getSigninIdAndPollSigninStatus,
    signinId,
    setSelectedRegion,
    handleSubmitPreemptiveForm,
    handleSigninDone,
    isFollowUpForm,
    questionProTaskStatus,
  } = useAmazonDataConnection({
    store,
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onProgressStep: setLoadingStep,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoToInitial,
  });

  // Keep the hook's region in sync with the chosen region.
  useEffect(() => {
    setSelectedRegion(region);
  }, [region, setSelectedRegion]);

  // ── Survey state ────────────────────────────────────────────────────────────
  const [surveyVersion, setSurveyVersion] = useState(FALLBACK_SURVEY_VERSION);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const [surveyReloadKey, setSurveyReloadKey] = useState(0);
  const surveyLoadedRef = useRef(false);

  useEffect(() => {
    if (screen !== 'survey' || surveyLoadedRef.current) return;
    surveyLoadedRef.current = true;
    setIsLoadingQuestions(true);
    let active = true;
    apiClient
      .getPostConnectSurveyQuestions(SURVEY_ID)
      .then((data) => {
        if (!active) return;
        setSurveyVersion(data.surveyVersion);
        setQuestions(data.questions);
        setAnswers(getInitialAnswers(data.questions));
      })
      .catch((loadError) => {
        clientLogger.info('Cleaning study questions load failed', {
          error: loadError,
        });
        if (active)
          setSurveyError('We could not load the survey. Please try again.');
        // Allow a retry: clear the guard so re-entering the survey re-fetches.
        surveyLoadedRef.current = false;
      })
      .finally(() => {
        if (active) setIsLoadingQuestions(false);
      });
    return () => {
      active = false;
    };
  }, [screen, surveyReloadKey]);

  const question = questions[currentIndex];
  const nextIndex = getNextVisibleIndex(questions, currentIndex, 1, answers);
  const canProceed =
    Boolean(question) &&
    canProceedFromQuestion(question, answers[currentIndex]);

  const updateAnswer = (answer: SurveyAnswer) => {
    setAnswers((current) => {
      const next = [...current];
      next[currentIndex] = answer;
      return next;
    });
    setSurveyError(null);
  };

  const submitSurvey = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSurveyError(null);
    try {
      await apiClient.submitPostConnectSurveyResponse({
        surveyVersion,
        response: serializeAnswers(questions, answers),
        submittedAtClient: new Date().toISOString(),
      });
      setScreen('done');
    } catch (submitError) {
      clientLogger.info('Cleaning study submit failed', { error: submitError });
      setSurveyError('We could not save your answers. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSurveyNext = () => {
    if (!canProceed || isSubmitting) return;
    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
      window.scrollTo(0, 0);
    } else {
      void submitSurvey();
    }
  };

  const handleSurveyBack = () => {
    const prevIndex = getNextVisibleIndex(questions, currentIndex, -1, answers);
    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex);
    } else {
      // back out of the survey to the (already connected) status screen
      setScreen('connect');
      setConnectStep('connected');
    }
  };

  // ── Participant code (standalone — no panel redirect) ───────────────────────
  const [participantCode] = useState(genParticipantCode);
  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    void navigator.clipboard?.writeText(participantCode).catch(() => {});
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  // ── URL bar rewriting per state ─────────────────────────────────────────────
  useEffect(() => {
    if (screen === 'welcome') rewriteUrl('/cleaning-study');
    else if (screen === 'survey') rewriteUrl('/cleaning-study/survey');
    else if (screen === 'done') rewriteUrl('/cleaning-study/done');
    else if (screen === 'connect') {
      if (connectStep === 'region' || connectStep === 'error')
        rewriteUrl('/cleaning-study/connect');
      else if (connectStep === 'signin')
        rewriteUrl('/cleaning-study/connect/signin');
      else if (connectStep === 'connecting')
        rewriteUrl('/cleaning-study/connect/loading');
      else if (connectStep === 'connected')
        rewriteUrl('/cleaning-study/connect/success');
    }
  }, [screen, connectStep]);

  // ── Unified progress bar ────────────────────────────────────────────────────
  // Unified stepper: connect is "Question 1 of N", survey questions are 2..N.
  const totalSteps = 1 + (questions.length || 5);
  const stepPosition =
    screen === 'connect'
      ? 1
      : screen === 'survey'
        ? currentIndex + 2
        : screen === 'done'
          ? totalSteps
          : 0;

  useEffect(() => {
    const pct = screen === 'welcome' ? null : (stepPosition / totalSteps) * 100;
    setOverride(pct);
    return () => setOverride(null);
  }, [screen, stepPosition, totalSteps, setOverride]);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (screen === 'welcome') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-[520px] animate-[enter_0.4s_cubic-bezier(0.16,1,0.3,1)_both]">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary mb-4">
            Clorox Cleaning Study
          </p>
          <h1 className="font-display font-bold text-[clamp(26px,5vw,40px)] text-ink leading-[1.13] tracking-[-0.03em] mb-4">
            Hi there 👋
            <br />
            Welcome to the study
          </h1>
          <p className="text-[16px] text-ink-muted leading-[1.6] mb-8 max-w-[440px]">
            Thanks for joining! We&apos;re studying how households choose and
            use everyday cleaning products — and your answers help Clorox make
            better ones.
          </p>
          <button
            data-testid="cleaning-study-get-started-button"
            onClick={() => {
              setConnectStep('region');
              setScreen('connect');
            }}
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-primary hover:bg-primary-deep text-white font-semibold text-[16px] rounded-xl transition-colors duration-150 active:scale-[0.985]"
          >
            Get started
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (screen === 'connect') {
    let content: ReactNode;
    if (connectStep === 'connecting') {
      content = (
        <LoadingPage
          autoComplete={false}
          initialStep={loadingStep}
          totalSteps={3}
          retailerName={RETAILER_CONFIGS['amazon']?.retailer_name ?? 'Amazon'}
        />
      );
    } else if (connectStep === 'connected') {
      content = <ConnectedView onContinue={() => setScreen('survey')} />;
    } else if (connectStep === 'signin') {
      content = (
        <div className="w-full pt-6">
          {!isFollowUpForm && (
            <>
              <h2 className="font-display font-bold text-[clamp(20px,3.5vw,24px)] tracking-[-0.02em] text-ink leading-[1.2] mb-5">
                Connect your Amazon account
              </h2>
              <WhyConnect />
            </>
          )}
          {/* SignInPage adds its own py-12/px-6 centering; cancel it so the card
              sits directly under the infobox instead of recentering with a gap. */}
          <div className="[&>div]:!py-0 [&>div]:!px-0">
            <SignInPage
              retailerName={
                RETAILER_CONFIGS['amazon']?.retailer_name ?? 'Amazon'
              }
              onStartConnection={getSigninIdAndPollSigninStatus}
              signinId={signinId}
              onSubmitPreemptiveForm={handleSubmitPreemptiveForm}
              showFollowUpForm={isFollowUpForm}
              questionProTaskStatus={questionProTaskStatus}
              requiresPassword={
                RETAILER_CONFIGS['amazon']?.requires_password ?? true
              }
              variant="inline"
              onBack={() => setConnectStep('region')}
              onCloseFollowUpForm={async () => {
                await apiClient.releaseSyncCapacity().catch(() => {});
                setConnectStep('region');
              }}
              onSigninDone={handleSigninDone}
            />
          </div>
        </div>
      );
    } else {
      // region (and error fallback)
      content = (
        <RegionStep
          stores={amazonStores}
          region={region}
          error={connectStep === 'error' ? connectError : null}
          onSelectRegion={setRegion}
          onContinue={() => {
            setConnectError(null);
            setConnectStep('signin');
          }}
        />
      );
    }
    return (
      <StepperShell position={1} total={totalSteps} maxWidth="max-w-[480px]">
        {content}
      </StepperShell>
    );
  }

  if (screen === 'survey') {
    if (surveyError && questions.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-5 py-8 text-center">
          <div className="max-w-[360px]">
            <h1 className="font-display text-2xl font-bold text-ink mb-3">
              Survey unavailable
            </h1>
            <p className="text-sm text-ink-muted mb-6">{surveyError}</p>
            <button
              type="button"
              data-testid="cleaning-study-survey-retry-button"
              onClick={() => {
                setSurveyError(null);
                setSurveyReloadKey((k) => k + 1);
              }}
              className="rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-deep"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    if (isLoadingQuestions || !question) {
      return (
        <div className="flex-1 flex items-center justify-center px-5 py-8">
          <div className="h-8 w-8 rounded-full border-2 border-rule border-t-primary animate-spin" />
        </div>
      );
    }
    return (
      <StepperShell position={currentIndex + 2} total={totalSteps}>
        <div className="w-full pt-6">
          <h2 className="font-display text-[clamp(20px,5vw,24px)] font-bold leading-[1.3] tracking-[-0.02em] text-ink mb-6">
            {question.prompt}
          </h2>
          {'helper' in question && question.helper && (
            <p className="-mt-4 mb-6 text-sm leading-[1.5] text-ink-muted">
              {question.helper}
            </p>
          )}

          <SurveyQuestionInput
            question={question}
            answer={answers[currentIndex]}
            onChange={updateAnswer}
          />

          {surveyError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {surveyError}
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              data-testid="cleaning-study-survey-back-button"
              onClick={handleSurveyBack}
              className="inline-flex items-center gap-2 px-0 py-2 text-sm font-medium text-ink-faint hover:text-ink-muted"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
            <button
              type="button"
              data-testid="cleaning-study-survey-next-button"
              onClick={handleSurveyNext}
              disabled={!canProceed || isSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {nextIndex >= questions.length
                ? isSubmitting
                  ? 'Saving…'
                  : 'Finish'
                : 'Next'}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </StepperShell>
    );
  }

  // done
  return (
    <DoneScreen code={participantCode} copied={copied} onCopy={copyCode} />
  );
}

// ── Shared stepper shell: "Question N of total" counter + progress bar that
// spans connect (step 1) and every survey question (steps 2..total). ──────────
function StepperShell({
  position,
  total,
  maxWidth = 'max-w-[560px]',
  children,
}: {
  position: number;
  total: number;
  // Match the counter/bar width to the content below it (connect uses 480, the
  // sign-in card width; survey questions use 560).
  maxWidth?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex-1 flex flex-col px-5 pt-8 pb-8">
      {/* Single shared-width column so the counter/bar and the content below it
          are guaranteed to line up — both live inside this one max-width box. */}
      <div className={`w-full ${maxWidth} mx-auto flex-1 flex flex-col`}>
        <div className="mb-3 flex items-center justify-end font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-ink-faint">
          <span>
            Question <b className="font-bold text-primary">{position}</b> of{' '}
            {total}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-rule-light">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-[600ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
            style={{ width: `${(position / total) * 100}%` }}
          />
        </div>
        <div className="flex-1 flex flex-col">{children}</div>
      </div>
    </div>
  );
}

// ── "Why connect" infobox shown above the sign-in card ────────────────────────
function WhyConnect() {
  const items = [
    {
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      title: 'Better products for you.',
      desc: "Your purchases help us make products you'll love.",
    },
    {
      icon: 'M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z',
      title: 'One-time & read-only.',
      desc: 'Your password is never seen or stored.',
    },
    {
      icon: 'M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65',
      title: 'Anonymous.',
      desc: 'Used only for research, never tied to your name.',
    },
  ];
  return (
    <div className="flex flex-col gap-4 mb-6 bg-paper border border-rule-light rounded-2xl px-5 py-4">
      {items.map((item) => (
        <div
          key={item.title}
          className="flex gap-3 items-start text-[13.5px] text-ink-muted leading-[1.5]"
        >
          <svg
            className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
          </svg>
          <span>
            <b className="text-ink-soft font-semibold">{item.title}</b>{' '}
            {item.desc}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Connected status (brief, before survey) ───────────────────────────────────
function ConnectedView({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="w-20 h-20 rounded-full bg-success-pale flex items-center justify-center text-success animate-[popIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]">
        <svg
          width="36"
          height="36"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 className="font-display font-bold text-[clamp(18px,3.5vw,24px)] tracking-[-0.02em] text-ink mt-7">
        Amazon connected
      </h2>
      <p className="text-[14px] text-ink-muted mt-1">
        Your purchases are verified — you&apos;re all set.
      </p>
      <button
        data-testid="cleaning-study-connected-continue-button"
        onClick={onContinue}
        className="mt-7 inline-flex items-center gap-2 bg-primary hover:bg-primary-deep text-white font-semibold text-[15px] px-7 py-3 rounded-xl transition-colors"
      >
        Continue
        <svg
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

// ── Region step ───────────────────────────────────────────────────────────────
function RegionStep({
  stores,
  region,
  error,
  onSelectRegion,
  onContinue,
}: {
  stores: StoreConfig[];
  region: 'US' | 'CA';
  error: string | null;
  onSelectRegion: (r: 'US' | 'CA') => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col pt-6">
      <div className="w-full animate-[enter_0.4s_cubic-bezier(0.16,1,0.3,1)_both]">
        <h2 className="font-display text-[clamp(22px,3vw,28px)] font-bold text-ink tracking-[-0.02em] leading-[1.2] mb-2">
          Where do you shop?
        </h2>
        <p className="text-[15px] text-ink-muted mb-8">
          Connect your Amazon account so we can verify your recent household
          purchases.
        </p>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 mb-8">
          {stores.map((option) => {
            const isSelected =
              (option.region as 'US' | 'CA' | undefined) === region ||
              (!option.region && region === 'US');
            return (
              <button
                key={option.retailer_key}
                data-testid={`region-select-${option.retailer_key}`}
                onClick={() =>
                  onSelectRegion((option.region as 'US' | 'CA') ?? 'US')
                }
                className={`flex items-center gap-4 px-5 py-4 rounded-xl border-[1.5px] text-left transition-all duration-150 ${
                  isSelected
                    ? 'border-primary bg-primary-pale'
                    : 'border-rule bg-surface hover:border-ink-faint'
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-surface' : 'bg-paper'
                  }`}
                >
                  {option.logo_url && (
                    <img
                      src={option.logo_url}
                      alt={option.retailer_name}
                      className="w-7 h-7 object-contain"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <span className="block font-semibold text-[15px] text-ink">
                    {option.label}
                  </span>
                  <span className="block text-[13px] text-ink-muted mt-0.5">
                    {option.domain}
                  </span>
                </div>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                    isSelected ? 'bg-primary' : 'border-2 border-rule'
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="w-3.5 h-3.5 text-white"
                      fill="currentColor"
                      viewBox="0 0 12 12"
                    >
                      <path d="M9.707 3.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L5 6.586l3.293-3.293a1 1 0 011.414 0z" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <button
          data-testid="region-select-continue-button"
          onClick={onContinue}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary hover:bg-primary-deep text-white font-body font-semibold text-[15px] rounded-full transition-colors duration-150 active:scale-[0.985]"
        >
          Continue
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Done screen (participant code, no redirect) ───────────────────────────────
function DoneScreen({
  code,
  copied,
  onCopy,
}: {
  code: string;
  copied: boolean;
  onCopy: () => void;
}) {
  const [showConfetti, setShowConfetti] = useState(true);
  const dims = useWindowDimensions(showConfetti);
  useEffect(() => {
    const t = window.setTimeout(() => setShowConfetti(false), 4500);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
      {showConfetti && (
        <ReactConfetti
          width={dims.width}
          height={dims.height}
          recycle={false}
          numberOfPieces={300}
          gravity={0.1}
        />
      )}
      <div className="w-20 h-20 rounded-full bg-success-pale flex items-center justify-center text-success animate-[popIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]">
        <svg
          width="36"
          height="36"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary mt-5 mb-2.5">
        Survey complete
      </p>
      <h1 className="font-display font-bold text-[clamp(24px,3vw,32px)] tracking-[-0.02em] text-ink mb-2.5">
        Thank you!
      </h1>
      <p className="text-[16px] text-ink-muted leading-[1.6] max-w-[400px]">
        Your responses to the Clorox study have been recorded. We appreciate you
        taking part.
      </p>
      <button
        data-testid="done-screen-copy-code-button"
        onClick={onCopy}
        className="mt-5 inline-flex items-center gap-2 text-[13px] text-ink-muted bg-paper border border-rule rounded-full px-4 py-2 hover:border-primary hover:bg-primary-pale transition-colors"
        title="Click to copy"
      >
        Participant code{' '}
        <b className="font-mono font-bold text-ink tracking-[0.08em]">{code}</b>
        <svg
          width="13"
          height="13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          className="text-primary"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h6a2 2 0 002-2v-2"
          />
        </svg>
      </button>
      {copied && (
        <p className="mt-2 text-[13px] text-success">Copied to clipboard</p>
      )}
    </div>
  );
}
